export type Tone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info';

export interface Descriptor {
  label: string;
  tone: Tone;
}

/**
 * Traducao de estado para etiqueta.
 *
 * O servidor entrega a chave do estado, e a apresentacao vive em um unico mapa. Sem
 * ele, cada tela traduz por conta propria e a mesma etapa aparece com nomes e cores
 * diferentes conforme o modulo em que a pessoa esta.
 */
const operacao: Record<string, Descriptor> = {
  // A cotacao da corretora antes do envio para analise (P-NCT).
  em_cotacao: { label: 'Em cotacao', tone: 'neutral' },
  aguardando_credito: { label: 'Aguardando credito', tone: 'info' },
  em_subscricao: { label: 'Em subscricao', tone: 'info' },
  em_juridico: { label: 'Em juridico', tone: 'info' },
  aguardando_comercial: { label: 'Aguardando comercial', tone: 'info' },
  minuta_gerada: { label: 'Minuta gerada', tone: 'brand' },
  aguardando_ccg: { label: 'Aguardando CCG', tone: 'brand' },
  aguardando_assinatura: { label: 'Aguardando assinatura', tone: 'brand' },
  em_emissao: { label: 'Em emissao', tone: 'brand' },
  emitida: { label: 'Emitida', tone: 'success' },
  declinada: { label: 'Declinada', tone: 'danger' },
};

const corretor: Record<string, Descriptor> = {
  novo: { label: 'Novo', tone: 'neutral' },
  em_documentacao: { label: 'Em documentacao', tone: 'info' },
  em_validacao: { label: 'Em validacao', tone: 'warning' },
  ativo: { label: 'Ativo', tone: 'success' },
  bloqueado: { label: 'Bloqueado', tone: 'danger' },
};

const situacaoExterna: Record<string, Descriptor> = {
  nao_consultado: { label: 'Nao consultado', tone: 'neutral' },
  regular: { label: 'Regular', tone: 'success' },
  irregular: { label: 'Irregular', tone: 'danger' },
  nao_encontrado: { label: 'Nao encontrado', tone: 'warning' },
};

const cadastro: Record<string, Descriptor> = {
  // Ramo, grupo ou modalidade da carga com aprovacao (P-CARGA), antes da ativacao.
  rascunho: { label: 'Rascunho', tone: 'warning' },
  ativo: { label: 'Ativo', tone: 'success' },
  inativo: { label: 'Inativo', tone: 'neutral' },
  bloqueado: { label: 'Bloqueado', tone: 'danger' },
};

// O vinculo da pessoa com a empresa guarda a situacao em ingles, porque a coluna
// existe desde a fundacao do schema e e lida por toda a autenticacao. A traducao
// mora aqui, e nao em cada tela: sem este mapa a etiqueta mostrava "active" cru.
const vinculo: Record<string, Descriptor> = {
  active: { label: 'Ativo', tone: 'success' },
  suspended: { label: 'Suspenso', tone: 'danger' },
  inactive: { label: 'Inativo', tone: 'neutral' },
};

// A situacao do convite e deduzida no servidor a partir das datas: ninguem precisa
// passar marcando o que venceu.
const convite: Record<string, Descriptor> = {
  pendente: { label: 'Pendente', tone: 'warning' },
  aceito: { label: 'Aceito', tone: 'success' },
  expirado: { label: 'Expirado', tone: 'neutral' },
  revogado: { label: 'Revogado', tone: 'danger' },
};

const prioridade: Record<string, Descriptor> = {
  baixa: { label: 'Baixa', tone: 'neutral' },
  normal: { label: 'Normal', tone: 'info' },
  alta: { label: 'Alta', tone: 'warning' },
  urgente: { label: 'Urgente', tone: 'danger' },
};

const credito: Record<string, Descriptor> = {
  em_analise: { label: 'Em analise', tone: 'info' },
  aprovado: { label: 'Aprovado', tone: 'success' },
  reprovado: { label: 'Reprovado', tone: 'danger' },
  pendente: { label: 'Pendente', tone: 'warning' },
};

const subscricao: Record<string, Descriptor> = {
  pendente: { label: 'Pendente', tone: 'warning' },
  aprovada: { label: 'Aprovada', tone: 'success' },
  reprovada: { label: 'Reprovada', tone: 'danger' },
};

const juridico: Record<string, Descriptor> = {
  nao_iniciado: { label: 'Nao iniciado', tone: 'neutral' },
  em_analise: { label: 'Em analise', tone: 'info' },
  pendencia: { label: 'Pendencia', tone: 'warning' },
  aprovado: { label: 'Aprovado', tone: 'success' },
};

const area: Record<string, Descriptor> = {
  credito: { label: 'Credito', tone: 'info' },
  subscricao: { label: 'Subscricao', tone: 'info' },
  juridico: { label: 'Juridico', tone: 'info' },
  comercial: { label: 'Comercial', tone: 'info' },
  documentos: { label: 'Documentos', tone: 'neutral' },
  operacional: { label: 'Operacional', tone: 'neutral' },
  // Origens do Follow up do OnPoint que nao sao etapa (P-COT-3).
  cadastro: { label: 'Cadastro', tone: 'brand' },
  pep: { label: 'PEP', tone: 'warning' },
  resseguro: { label: 'Resseguro', tone: 'brand' },
};

const pendencia: Record<string, Descriptor> = {
  aberta: { label: 'Aberta', tone: 'warning' },
  resolvida: { label: 'Resolvida', tone: 'success' },
};

const contraparte: Record<string, Descriptor> = {
  ativa: { label: 'Ativa', tone: 'success' },
  ativo: { label: 'Ativo', tone: 'success' },
  em_homologacao: { label: 'Em homologacao', tone: 'warning' },
  inativa: { label: 'Inativa', tone: 'neutral' },
  inativo: { label: 'Inativo', tone: 'neutral' },
};

const faixa: Record<string, Descriptor> = {
  ativa: { label: 'Ativa', tone: 'success' },
  inativa: { label: 'Inativa', tone: 'neutral' },
};

const ajuste: Record<string, Descriptor> = {
  agravo: { label: 'Agravo', tone: 'warning' },
  desconto: { label: 'Desconto', tone: 'success' },
};

const apolice: Record<string, Descriptor> = {
  vigente: { label: 'Vigente', tone: 'success' },
  vencida: { label: 'Vencida', tone: 'warning' },
  cancelada: { label: 'Cancelada', tone: 'danger' },
  // Situacoes da lista de apolices do OnPoint (P-APO-1). "Emitida" e o nome do OnPoint
  // para a vigente; "Aguardando Emissao" e a operacao em emissao, sem numero; a
  // "Rejeitada" nao e registrada no SeguraCore.
  emitida: { label: 'Emitida', tone: 'success' },
  aguardando_emissao: { label: 'Aguardando Emissao', tone: 'brand' },
  rejeitada: { label: 'Rejeitada', tone: 'danger' },
};

/** Situacao informada da parcela da apolice (P-APO-1). */
const parcela: Record<string, Descriptor> = {
  em_aberto: { label: 'Em aberto', tone: 'warning' },
  paga: { label: 'Paga', tone: 'success' },
  vencida: { label: 'Vencida', tone: 'danger' },
  cancelada: { label: 'Cancelada', tone: 'neutral' },
};

const solicitacao: Record<string, Descriptor> = {
  solicitado: { label: 'Solicitado', tone: 'warning' },
  aprovado: { label: 'Aprovado', tone: 'success' },
  recusado: { label: 'Recusado', tone: 'danger' },
};

const endosso: Record<string, Descriptor> = {
  valor: { label: 'Valor garantido', tone: 'info' },
  prazo: { label: 'Prazo de vigencia', tone: 'info' },
  tomador: { label: 'Troca de tomador', tone: 'brand' },
  dados: { label: 'Correcao de dados', tone: 'neutral' },
};

const renovacao: Record<string, Descriptor> = {
  pendente: { label: 'Pendente', tone: 'warning' },
  em_cotacao: { label: 'Em cotacao', tone: 'info' },
  renovada: { label: 'Renovada', tone: 'success' },
  nao_renovada: { label: 'Nao renovada', tone: 'neutral' },
};

const sinistro: Record<string, Descriptor> = {
  aberto: { label: 'Aberto', tone: 'warning' },
  em_analise: { label: 'Em analise', tone: 'info' },
  pago: { label: 'Pago', tone: 'success' },
  negado: { label: 'Negado', tone: 'danger' },
};

const repasse: Record<string, Descriptor> = {
  a_receber: { label: 'A receber', tone: 'warning' },
  recebida: { label: 'Recebida', tone: 'info' },
  paga: { label: 'Paga', tone: 'success' },
  cancelada: { label: 'Cancelada', tone: 'neutral' },
};

// A situacao da empresa e da funcionalidade e gravada em ingles desde a fundacao do
// schema, como a do vinculo. A administracao da plataforma le as duas, e a traducao
// fica aqui pelo mesmo motivo: uma tela so que esquecesse o mapa mostraria "active".
const empresa: Record<string, Descriptor> = {
  active: { label: 'Ativa', tone: 'success' },
  suspended: { label: 'Suspensa', tone: 'danger' },
  closed: { label: 'Encerrada', tone: 'neutral' },
};

const funcionalidade: Record<string, Descriptor> = {
  active: { label: 'Ativa', tone: 'success' },
  trial: { label: 'Em avaliacao', tone: 'info' },
  suspended: { label: 'Suspensa', tone: 'danger' },
  expired: { label: 'Expirada', tone: 'warning' },
};

// Versao com aprovacao (shared/versioning): a configuracao da modalidade (P-PAR-1) e
// as series que vierem depois.
const versao: Record<string, Descriptor> = {
  rascunho: { label: 'Rascunho', tone: 'warning' },
  ativa: { label: 'Ativa', tone: 'success' },
  revogada: { label: 'Revogada', tone: 'neutral' },
};

// Cotacao na operacao (P-COT-1): a situacao da cotacao do OnPoint, que nao e a etapa
// da esteira, e o tipo da cotacao.
const cotacao: Record<string, Descriptor> = {
  cotacao: { label: 'Cotacao', tone: 'neutral' },
  em_analise: { label: 'Em analise', tone: 'info' },
  aguardando_corretora: { label: 'Aguardando corretora', tone: 'warning' },
  aguardando_emissao_corretora: {
    label: 'Aguardando Emissao (Corretora)',
    tone: 'brand',
  },
  aguardando_emissao_seguradora: {
    label: 'Aguardando Emissao (Seguradora)',
    tone: 'brand',
  },
  aguardando_pagamento_boleto: {
    label: 'Aguardando pagamento de boleto',
    tone: 'warning',
  },
  emitida: { label: 'Emitida', tone: 'success' },
  recusada: { label: 'Recusada', tone: 'danger' },
  rejeitada: { label: 'Rejeitada', tone: 'danger' },
  cancelada: { label: 'Cancelada', tone: 'neutral' },
};

const tipoCotacao: Record<string, Descriptor> = {
  apolice: { label: 'Apolice', tone: 'neutral' },
  endosso: { label: 'Endosso', tone: 'info' },
  renovacao: { label: 'Renovacao', tone: 'brand' },
};

// Esteiras (P-EST-1): o tipo da coluna, a origem do registro do Follow up e a origem
// do registro do historico do cartao.
const colunaEsteira: Record<string, Descriptor> = {
  inicial: { label: 'Entrada', tone: 'info' },
  andamento: { label: 'Em andamento', tone: 'info' },
  aprovado: { label: 'Aprovado', tone: 'success' },
  reprovado: { label: 'Reprovado', tone: 'danger' },
  cancelado: { label: 'Cancelado', tone: 'neutral' },
};

const origemFollowUp: Record<string, Descriptor> = {
  cadastro: { label: 'Cadastro', tone: 'brand' },
  credito: { label: 'Credito', tone: 'info' },
  nomeacao: { label: 'Nomeacao', tone: 'brand' },
  pep: { label: 'PEP', tone: 'warning' },
  resseguro: { label: 'Resseguro', tone: 'brand' },
  subscricao: { label: 'Subscricao', tone: 'info' },
  cotacao: { label: 'Cotacao', tone: 'neutral' },
  outra: { label: 'Esteira', tone: 'neutral' },
};

const origemCartao: Record<string, Descriptor> = {
  quadro: { label: 'Quadro', tone: 'brand' },
  operacao: { label: 'Operacao', tone: 'info' },
  analise: { label: 'Parecer', tone: 'info' },
  pendencia: { label: 'Pendencia', tone: 'warning' },
  carga: { label: 'Carga da esteira', tone: 'neutral' },
  // P-EST-2: a raia do motor e o pedido (nomeacao, revisao de limite).
  motor: { label: 'Motor de credito', tone: 'warning' },
  pedido: { label: 'Pedido', tone: 'brand' },
};

const eventoCartao: Record<string, Descriptor> = {
  entrada: { label: 'Entrada na esteira', tone: 'info' },
  movimento: { label: 'Movimento', tone: 'brand' },
  responsavel: { label: 'Responsavel', tone: 'neutral' },
  prioridade: { label: 'Prioridade', tone: 'neutral' },
  saida: { label: 'Saida da esteira', tone: 'neutral' },
  // P-EST-2: a regra do motor acrescentada e o envio ao corretor.
  regra: { label: 'Regra do motor', tone: 'warning' },
  envio_corretor: { label: 'Enviado ao corretor', tone: 'success' },
};

// Situacao do pedido do cartao (P-EST-2): a nomeacao do corretor e a revisao de limite.
const pedidoCartao: Record<string, Descriptor> = {
  pendente: { label: 'Pendente', tone: 'warning' },
  aprovada: { label: 'Aprovada', tone: 'success' },
  concluida: { label: 'Concluida', tone: 'success' },
  recusada: { label: 'Recusada', tone: 'danger' },
  cancelada: { label: 'Cancelada', tone: 'neutral' },
};

// Situacao e tipo do endosso do catalogo (P-END-1). A situacao inclui as da solicitacao
// e as do assistente: rascunho, em analise na esteira, emitido e cancelado.
const situacaoEndosso: Record<string, Descriptor> = {
  rascunho: { label: 'Rascunho', tone: 'neutral' },
  solicitado: { label: 'Solicitado', tone: 'warning' },
  em_analise: { label: 'Em analise na esteira', tone: 'info' },
  aprovado: { label: 'Aprovado', tone: 'success' },
  emitido: { label: 'Emitido', tone: 'success' },
  recusado: { label: 'Recusado', tone: 'danger' },
  cancelado: { label: 'Cancelado', tone: 'neutral' },
};

const tipoEndosso: Record<string, Descriptor> = {
  neutro: { label: 'Endosso neutro', tone: 'neutral' },
  alteracao: { label: 'Endosso de alteracao', tone: 'info' },
  cancelamento: { label: 'Endosso de cancelamento', tone: 'danger' },
  aniversario: { label: 'Endosso de aniversario', tone: 'brand' },
};

// --- Acompanhamento da cotacao (P-COT-3) -----------------------------------------

/** Grupo da situacao da linha do tempo (Cadastro, PEP, Credito... do OnPoint). */
const grupoSituacao: Record<string, Descriptor> = {
  cotacao: { label: 'Cotacao', tone: 'neutral' },
  cadastro: { label: 'Cadastro', tone: 'brand' },
  pep: { label: 'PEP', tone: 'warning' },
  credito: { label: 'Credito', tone: 'info' },
  subscricao: { label: 'Subscricao', tone: 'info' },
  resseguro: { label: 'Resseguro', tone: 'brand' },
  juridico: { label: 'Juridico', tone: 'info' },
  comercial: { label: 'Comercial', tone: 'info' },
  emissao: { label: 'Emissao', tone: 'success' },
};

/** Status do item da linha do tempo (legenda Aprovado e Recusado do OnPoint). */
const statusSituacao: Record<string, Descriptor> = {
  aprovado: { label: 'Aprovado', tone: 'success' },
  recusado: { label: 'Recusado', tone: 'danger' },
  pendente: { label: 'Pendente', tone: 'warning' },
  resolvido: { label: 'Resolvido', tone: 'success' },
  em_andamento: { label: 'Em andamento', tone: 'info' },
  cancelado: { label: 'Cancelado', tone: 'danger' },
  informativo: { label: 'Informacao', tone: 'neutral' },
};

/** Lado de quem executou (legenda Seguradora e Corretor do OnPoint). */
const ladoSituacao: Record<string, Descriptor> = {
  seguradora: { label: 'Seguradora', tone: 'brand' },
  corretor: { label: 'Corretor', tone: 'info' },
  sistema: { label: 'Sistema', tone: 'neutral' },
};

/** Fase da linha do tempo da cotacao. */
const faseCotacao: Record<string, Descriptor> = {
  cotacao: { label: 'Cotacao', tone: 'info' },
  proposta: { label: 'Proposta', tone: 'brand' },
  emissao: { label: 'Emissao', tone: 'success' },
};

/** Origem do documento (coluna "Origem" dos documentos da cotacao). */
const origemDocumento: Record<string, Descriptor> = {
  corretor: { label: 'Corretor', tone: 'info' },
  seguradora: { label: 'Seguradora', tone: 'brand' },
  sistema: { label: 'Sistema', tone: 'neutral' },
};

/** Motivo da linha do historico de alteracoes. */
const historicoCotacao: Record<string, Descriptor> = {
  abertura: { label: 'Abertura', tone: 'info' },
  alteracao: { label: 'Alteracao', tone: 'brand' },
  reinicio: { label: 'Reinicio', tone: 'warning' },
  carga: { label: 'Carga inicial', tone: 'neutral' },
};

const mapas = {
  operacao,
  corretor,
  situacaoExterna,
  cadastro,
  vinculo,
  convite,
  prioridade,
  credito,
  subscricao,
  juridico,
  area,
  pendencia,
  contraparte,
  faixa,
  ajuste,
  apolice,
  repasse,
  solicitacao,
  endosso,
  renovacao,
  sinistro,
  empresa,
  funcionalidade,
  versao,
  cotacao,
  tipoCotacao,
  parcela,
  colunaEsteira,
  origemFollowUp,
  origemCartao,
  eventoCartao,
  pedidoCartao,
  situacaoEndosso,
  tipoEndosso,
  grupoSituacao,
  statusSituacao,
  ladoSituacao,
  faseCotacao,
  origemDocumento,
  historicoCotacao,
} as const;

export type StatusKind = keyof typeof mapas;

/** Devolve o rotulo legivel de um estado, para uso fora da etiqueta. */
export function statusLabel(kind: StatusKind, value: string): string {
  return mapas[kind][value]?.label ?? value;
}

export function statusDescriptor(
  kind: StatusKind,
  value: string,
): Descriptor | undefined {
  return mapas[kind][value];
}
